import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import BedbankMarginText from 'components/OfferPage/Bedbank/BedbankMargin'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { buildSuggestedDatesString } from 'lib/search/searchUtils'
import OfferListEventsContext, {
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import Group from 'components/utils/Group'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  offerPrice: number
  offerMemberPrice: number
  isFlexibleSearch?: string
  suggestedDates?: {
    checkIn: string
    checkOut: string
  }
  nightsToDisplay: number
  promoteAsBundle: boolean
  saleUnit: string
  rooms: Array<App.Occupants>
  discountPercentage: number
  value: number
  hotelNights: number
  offerLocations: Array<string>
  displayMargin?: boolean
  margin?: number
  marginAud?: number
  supplier?: string
  directSearchPrices?: App.OfferListMetadataPricing
  shownRate?: {
    mobilePromotion?: boolean
  }
  bundlePropertyFees?: number
  propertyFees?: number
  bundleTaxesAndFees?: number
  taxesAndFees?: number
  bundlePrice: number
  hotelPrice: number
  flightPrice?: number
  bundleMemberPrice?: number
  hotelMemberPrice: number
  luxPlusLPPEnabled: boolean
  offerUrl: string
  hidePrice?: boolean
  hideIncludesTaxesAndFees?: boolean
  showPayAtPropertyPill?: boolean
  viewPriceInPoints?: boolean
}

function BedbankSearchTileGarden({
  offer,
  offerPrice,
  offerMemberPrice,
  isFlexibleSearch,
  suggestedDates,
  nightsToDisplay,
  promoteAsBundle,
  saleUnit,
  rooms,
  discountPercentage,
  value,
  hotelNights,
  offerLocations,
  displayMargin,
  margin,
  marginAud,
  supplier,
  directSearchPrices,
  shownRate,
  bundlePropertyFees,
  propertyFees,
  bundleTaxesAndFees,
  taxesAndFees,
  bundlePrice,
  hotelPrice,
  flightPrice,
  bundleMemberPrice,
  hotelMemberPrice,
  luxPlusLPPEnabled,
  offerUrl,
  hidePrice = false,
  hideIncludesTaxesAndFees = false,
  showPayAtPropertyPill = false,
  viewPriceInPoints = false,
}: Props) {
  const intl = useIntl()
  const { hasSelectedBurnToggle } = useSearchBurnEnabled()
  const burnResult = useSearchBurnPoints(offerPrice, getLoyaltyProductType(offer) ?? 'hotel_lpp_eps', undefined, { viewPriceInPoints: viewPriceInPoints && !hasSelectedBurnToggle })
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  useEffect(() => {
    dispatchOfferListEvent({
      type: OfferListEvents.pricingCalculated,
      availability: true,
      roomRateId: undefined,
      price: hidePrice ? undefined : offerPrice,
      memberPrice: hidePrice ? undefined : offerMemberPrice,
      duration: nightsToDisplay,
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const priceCaptionText = useMemo(() => {
    const datesOrNights = isFlexibleSearch && suggestedDates
      ? buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
      : intl.formatMessage(
          {
            id: 'm5L0sUHF1GtB591lBNfZ',
            defaultMessage: '{nights, plural, one {# night} other {# nights}}',
            description: 'Night count for bedbank search tile price caption in the search interface',
          },
          { nights: nightsToDisplay },
        )
    if (promoteAsBundle) {
      return intl.formatMessage(
        {
          id: 'AKw8DCa-Jwzdz1UkVaeB',
          defaultMessage: '{datesOrNights} & flights from',
          description: 'Price caption with nights/dates and flights for bedbank search tile',
        },
        { datesOrNights },
      )
    }
    return intl.formatMessage(
      {
        id: 'FLVfJrwOOoEO_TBBIDUX',
        defaultMessage: '{datesOrNights} from',
        description: 'Price caption with nights/dates for bedbank search tile',
      },
      { datesOrNights },
    )
  }, [isFlexibleSearch, suggestedDates, nightsToDisplay, promoteAsBundle, intl])

  const _propertyFees = promoteAsBundle ? bundlePropertyFees : propertyFees
  const _hotelPrice = promoteAsBundle ? bundlePrice : hotelPrice
  const _hotelMemberPrice = promoteAsBundle ?
    bundleMemberPrice :
    hotelMemberPrice

  const bedbankPriceSaleUnit = (saleUnit === 'night' || saleUnit === 'person') ? saleUnit : 'total'

  let bedbankPriceDisplay: React.ReactNode
  if (burnResult.enabled) {
    bedbankPriceDisplay = <LoyaltyBurnPriceDisplay
      totalPrice={offerPrice}
      pointsToBurn={burnResult.pointsToBurn}
      priceInPoints={burnResult.priceInPoints}
      memberPrice={offerMemberPrice}
      size="L"
      saleUnit={bedbankPriceSaleUnit}
      hideZeroCash={burnResult.hideZeroCash}
    />
  } else {
    bedbankPriceDisplay = <LuxPlusPriceStack
      size="L"
      price={offerPrice}
      memberPrice={offerMemberPrice}
      saleUnit={saleUnit}
    />
  }

  if (hidePrice) {
    return null
  }

  return (
    <>
      {!!offerPrice && <>
        <Group direction="vertical" gap={8} horizontalAlign="start">
          {showPayAtPropertyPill && (
            <PayAtPropertyPill />
          )}
          <PriceRowPriceCaption>
            {priceCaptionText}
          </PriceRowPriceCaption>
        </Group>
        {bedbankPriceDisplay}
        {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
        {discountPercentage >= 5 && <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={value}
          discountPercentage={discountPercentage}
        />}
      </>}

      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
      />

      {displayMargin && (
        <BedbankMarginText
          margin={margin!}
          marginAud={marginAud!}
          supplier={supplier!}
          type="bedbank_estimate"
        />
      )}
      {(directSearchPrices || (shownRate && hotelNights)) && (
        <OfferPriceDetailsRow
          trigger="price-row"
          triggerSize="M"
          offer={offer}
          rooms={rooms}
          propertyFees={_propertyFees}
          taxesAndFees={promoteAsBundle ? bundleTaxesAndFees : taxesAndFees}
          hotelPrice={_hotelPrice - (_propertyFees ?? 0)}
          flightPrice={promoteAsBundle ? flightPrice : undefined}
          hotelMemberPrice={
            _hotelMemberPrice && _propertyFees ?
              _hotelMemberPrice - _propertyFees :
              undefined
          }
          showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
          duration={hotelNights}
          cta={
            <TextButton kind="primary" fit="flex" to={offerUrl}>
              {intl.formatMessage({
                id: 'P2EB8s5UpskzMpdaUh_c',
                defaultMessage: 'View Offer',
                description: 'CTA button in bedbank price details row to view the offer',
              })}
            </TextButton>
          }
          context="search"
          hasMobilePromotion={shownRate?.mobilePromotion}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
        />
      )}
    </>
  )
}

export default BedbankSearchTileGarden
